import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosPromise } from "axios";
import {
  MePreferenceProductsApi,
  type MePreferenceProductsApiV2MePreferencesProductsGetRequest,
  type MePreferenceProductsApiV2MePreferencesProductsStatusGetRequest,
  type ProductPreferences,
  Sorting,
} from "mws-ts-v2-sdk";

import { getUniversalAxios } from "~/utils/axios/getUniversalAxios";

const getMePreferenceProductsApi = () => {
  return new MePreferenceProductsApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    getUniversalAxios(),
  );
};

const getProductPreferences = async (
  params: MePreferenceProductsApiV2MePreferencesProductsGetRequest,
): AxiosPromise<ProductPreferences> => {
  return await getMePreferenceProductsApi().v2MePreferencesProductsGet({
    ...params,
    sortBy: params?.sortBy ?? Sorting.Descending,
    sort: params?.sort ?? "EndDate",
  });
};

export const followedProductsListQueryKeys = createQueryKeys(
  "followedProducts",
  {
    list: (
      params: MaybeRef<MePreferenceProductsApiV2MePreferencesProductsGetRequest>,
    ) => ({
      queryKey: [params],
      queryFn: async () => {
        const response = await getProductPreferences(unref(params));

        return response.data;
      },
    }),
    status: (
      params: MaybeRefOrGetter<MePreferenceProductsApiV2MePreferencesProductsStatusGetRequest>,
    ) => ({
      queryKey: [params],
      queryFn: async () => {
        const response =
          await getMePreferenceProductsApi().v2MePreferencesProductsStatusGet(
            toValue(params),
          );

        return response.data;
      },
    }),
  },
);

export const useFollowedProductsQuery = () => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const { isLoggedIn } = useAuth();

  const params = computed(() => ({
    pageSize: 1000,
    followed: true,
    sortBy: Sorting.Ascending,
    xPlatformKey: platformId.value,
  }));

  return useQuery({
    ...followedProductsListQueryKeys.list(params),
    refetchInterval: 60000,
    enabled: isLoggedIn,
  });
};
